import { useSearchParams } from 'react-router-dom'
import { getEncryptedText, getDecryptedData, isEncryptedString } from '../../lib/encryption'
import { ApiResponse, ApiRequestOptions } from '../types'

// Custom API Error class
export class ApiError extends Error {
  status?: number
  statusText?: string
  data?: any
  code?: string

  constructor(options: {
    message: string
    status?: number
    statusText?: string
    data?: any
    code?: string
  }) {
    super(options.message)
    this.name = 'ApiError'
    this.status = options.status
    this.statusText = options.statusText
    this.data = options.data
    this.code = options.code
  }
}

export class ApiClient {
  private baseURL: string
  private timeout: number
  private defaultHeaders: Record<string, string>
  //private token: string | null

  constructor(config: { baseURL?: string; timeout?: number; headers?: Record<string, string> } = {}) {
    this.baseURL = config.baseURL || 'https://api-uat.mytatva.in'
    this.timeout = config.timeout || 60000
    this.defaultHeaders = {
      'Accept': '*/*',
      'Content-Type': 'text/plain',
      ...config.headers
    }
    //const [searchParams] = useSearchParams();
    //this.token = searchParams.get('token');
  }

  private getAuthToken(): string | null {
    // First try to get token from localStorage
    const localToken = localStorage.getItem('auth_token');
    if (localToken) {
      return localToken;
    }

    // If not in localStorage, try to get from URL parameters (for embedded iframe)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      return urlToken;
    }
    return null;
  }

  private handleAuthExpiration(): void {
    localStorage.clear()
    window.location.href = '/'
    console.warn('Session expired. Please log in again.')
    window.location.reload()
  }

  private buildQueryString(params: Record<string, string | number | boolean>): string {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    return searchParams.toString()
  }

  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const baseUrl = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`
    if (params && Object.keys(params).length > 0) {
      const queryString = this.buildQueryString(params)
      return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${queryString}`
    }
    return baseUrl
  }

  private prepareHeaders(options: ApiRequestOptions, token: string | null = null): Record<string, string> {
    const headers = { ...this.defaultHeaders, ...options.headers }
    if (options.requireAuth !== false) {
      const authToken = this.getAuthToken()
      if (authToken) {
        headers['token'] = authToken
      } else if (token) {
        headers['token'] = token
      }
    }

    // Remove Content-Type for FormData (let browser set it with boundary)
    if (options.body instanceof FormData) {
      delete headers['Content-Type']
    }

    return headers
  }

  private prepareBody(body: any, encrypted: boolean = false): string | FormData {
    if (!body) return ''
    
    // Handle FormData (for file uploads)
    if (body instanceof FormData) {
      if (encrypted) {
        console.warn('FormData cannot be encrypted. Encryption will be skipped for file uploads.')
      }
      return body
    }
    
    if (encrypted) {
      try {
        return getEncryptedText(body)
      } catch {
        throw new ApiError({
          message: 'Failed to encrypt request body',
          code: 'ENCRYPTION_FAILED'
        })
      }
    }
    
    return typeof body === 'object' ? JSON.stringify(body) : String(body)
  }

  private async parseResponse(response: Response, encrypted: boolean = false): Promise<any> {
    const contentType = response.headers.get('content-type')
    
    // Handle different content types
    let parsedData: any
    if (contentType?.includes('application/json')) {
      parsedData = await response.json()
    } else if (contentType?.includes('application/octet-stream') || contentType?.includes('image/') || contentType?.includes('application/pdf')) {
      // Return blob for binary data (don't try to decrypt)
      return await response.blob()
    } else {
      parsedData = await response.text()
    }
    
    if (encrypted && parsedData) {
      try {
        if (typeof parsedData === 'object' && parsedData.encryptedData) {
          return getDecryptedData(parsedData.encryptedData)
        } else if (typeof parsedData === 'string' && isEncryptedString(parsedData)) {
          return getDecryptedData(parsedData)
        } else {
          console.warn('Response was expected to be encrypted but does not appear to be encrypted')
          return parsedData
        }
      } catch {
        throw new ApiError({
          message: 'Failed to decrypt response data',
          code: 'DECRYPTION_FAILED'
        })
      }
    }
    
    return parsedData
  }

  private async makeRequest<T = any>(
    method: string,
    endpoint: string,
    body?: any,
    options: ApiRequestOptions = {},
    token: string | null = null
  ): Promise<ApiResponse<T>> {
    const url = this.buildURL(endpoint, options.params)
    const headers = this.prepareHeaders({ ...options, body }, token)
    const requestBody = method !== 'GET' ? this.prepareBody(body, options.encrypted) : undefined

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || this.timeout)

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: requestBody,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (response.status === 401) {
        this.handleAuthExpiration()
        throw new ApiError({
          message: 'Authentication failed',
          status: 401,
          statusText: 'Unauthorized'
        })
      }

      if (!response.ok) {
        const errorJson = await this.parseResponse(response, options.encrypted)
        if (errorJson && errorJson.code == 0) {
          return {
            data: errorJson,
            status: response.status,
            statusText: response.statusText
          }
        }
        throw new ApiError({
          message: `HTTP error! status: ${response.status}`,
          status: response.status,
          statusText: response.statusText
        })
      }

      const data = await this.parseResponse(response, options.encrypted)

      return {
        data,
        status: response.status,
        statusText: response.statusText
      }
    } catch (error: any) {
      clearTimeout(timeoutId)
      
      if (error.name === 'AbortError') {
        throw new ApiError({
          message: 'Request timeout',
          code: 'TIMEOUT'
        })
      }

      if (error instanceof ApiError) {
        throw error
      }

      throw new ApiError({
        message: error.message || 'Network error',
        code: 'NETWORK_ERROR'
      })
    }
  }

  // HTTP Methods
  async get<T = any>(endpoint: string, options: ApiRequestOptions = {}, token: string | null = null): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('GET', endpoint, undefined, options, token)
  }

  async post<T = any>(endpoint: string, body?: any, options: ApiRequestOptions = {}, token: string | null = null): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('POST', endpoint, body, options, token)
  }

  async put<T = any>(endpoint: string, body?: any, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PUT', endpoint, body, options)
  }

  async patch<T = any>(endpoint: string, body?: any, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('PATCH', endpoint, body, options)
  }

  async delete<T = any>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>('DELETE', endpoint, undefined, options)
  }

  async upload<T = any>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value))
      })
    }

    return this.makeRequest<T>('POST', endpoint, formData, options)
  }

  async download(endpoint: string, filename?: string, options: ApiRequestOptions = {}): Promise<void> {
    const response = await this.makeRequest<Blob>('GET', endpoint, undefined, options)
    
    // Create download link
    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
} 